import React, { useMemo } from 'react';
import Modal from './Modal';
import { CDM_COLUMNS, DIFF_COLUMNS, voteVal } from '../utils/deriveSession';

/**
 * Facilitator-only participation overview, computed client-side from the
 * session data already in memory. Grouped by display name (names are
 * self-reported labels, not verified identities).
 */
export default function AnalyticsModal({ cdmRows, diffRows, onlineUsers, onClose }) {
  const participants = useMemo(() => {
    const map = new Map();
    const entry = (name) => {
      const key = name || '(unnamed)';
      if (!map.has(key)) map.set(key, { name: key, items: 0, itemsVoted: 0, voteMagnitude: 0 });
      return map.get(key);
    };
    const scan = (rows, cols) => rows.forEach((row) => cols.forEach((col) => (row[col] || []).forEach((item) => {
      entry(item.addedBy).items += 1;
      Object.entries(item.voters || {}).forEach(([key, v]) => {
        const value = voteVal(v);
        if (!value) return;
        const name = (v && typeof v === 'object' && v.name) || key;
        const p = entry(name);
        p.itemsVoted += 1;
        p.voteMagnitude += Math.abs(value);
      });
    })));
    scan(cdmRows, CDM_COLUMNS);
    scan(diffRows, DIFF_COLUMNS);
    map.delete('Class'); // merged individual-phase items are attributed to "Class"
    return [...map.values()].sort((a, b) => (b.items + b.voteMagnitude) - (a.items + a.voteMagnitude));
  }, [cdmRows, diffRows]);

  return (
    <Modal title="Participation" onClose={onClose} wide>
      <p className="text-meta text-ink-2 mb-3">
        {onlineUsers.length} online now · {participants.length} contributor{participants.length === 1 ? '' : 's'} total.
        Names are self-reported labels.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-body">
          <thead>
            <tr className="text-left text-meta text-ink-2 uppercase tracking-wide border-b">
              <th className="py-2 pr-3">Name</th>
              <th className="py-2 px-3 text-right">Items added</th>
              <th className="py-2 px-3 text-right">Items voted on</th>
              <th className="py-2 pl-3 text-right">Total votes cast</th>
            </tr>
          </thead>
          <tbody>
            {participants.map((p) => (
              <tr key={p.name} className="border-b border-line">
                <td className="py-2 pr-3 font-medium text-ink">
                  {p.name}
                  {onlineUsers.includes(p.name) && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-flag-green align-middle" title="Online now" />}
                </td>
                <td className="py-2 px-3 text-right text-ink">{p.items}</td>
                <td className="py-2 px-3 text-right text-ink">{p.itemsVoted}</td>
                <td className="py-2 pl-3 text-right text-ink">{p.voteMagnitude}</td>
              </tr>
            ))}
            {!participants.length && (
              <tr><td colSpan={4} className="py-6 text-center text-ink-2 text-meta">No contributions yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
